import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const ARTICLES_LABELS = {
  cartons_ont: 'Cartons ONT',
  sachets_orange: 'Sachets Jarretière Orange',
  sachets_blanc: 'Sachets Jarretière Blanche',
  sachets_violet: 'Sachets Jarretière Violet',
  sachets_gris: 'Sachets Jarretière Gris',
  nb_pto: 'Nombre de PTO',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function CommandeONT() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const [confirming, setConfirming] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/ont-orders/admin')
      .then(r => setOrders(r.data.orders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async (id) => {
    setConfirming(id);
    try {
      await api.put(`/ont-orders/admin/${id}/confirm`);
      load();
    } catch {
    } finally {
      setConfirming(null);
    }
  };

  const filtered = filter === 'tous' ? orders : orders.filter(o => o.statut === filter);

  const counts = {
    tous: orders.length,
    en_cours: orders.filter(o => o.statut === 'en_cours').length,
    confirme: orders.filter(o => o.statut === 'confirme').length,
  };

  return (
    <div style={{ padding: '28px 32px', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', margin: 0 }}>Commandes ONT</h1>
        <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>Commandes de matériel passées par les techniciens</p>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['tous', 'Toutes'], ['en_cours', 'En cours'], ['confirme', 'Réceptionnées']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            style={{
              padding: '6px 16px', borderRadius: 20, border: `1.5px solid ${filter === key ? 'var(--accent)' : 'var(--border)'}`,
              background: filter === key ? 'var(--accent)' : 'var(--bg2)',
              color: filter === key ? '#fff' : 'var(--text)',
              fontWeight: filter === key ? 700 : 400, fontSize: 13, cursor: 'pointer',
            }}
          >
            {label} <span style={{ opacity: 0.7 }}>({counts[key]})</span>
          </button>
        ))}
        <button onClick={load} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 12, cursor: 'pointer' }}>↻ Actualiser</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Aucune commande</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filtered.map(order => (
            <div key={order.id} style={{ background: 'var(--bg2)', border: `1.5px solid ${order.statut === 'confirme' ? '#3B6D11' : 'var(--border)'}`, borderRadius: 12, padding: '18px 20px' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{order.technicien_nom}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Passée le {formatDate(order.date_commande)}</div>
                  {order.date_confirmation && (
                    <div style={{ fontSize: 12, color: '#3B6D11', marginTop: 2 }}>
                      Réceptionnée le {formatDate(order.date_confirmation)} — par {order.confirme_par === 'admin' ? 'Admin' : 'Technicien'}
                    </div>
                  )}
                </div>
                <span style={{
                  padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: order.statut === 'confirme' ? '#EAF3DE' : '#FFF8E1',
                  color: order.statut === 'confirme' ? '#27500A' : '#7d5a00',
                  border: `1px solid ${order.statut === 'confirme' ? '#3B6D11' : '#F9CA24'}`,
                }}>
                  {order.statut === 'confirme' ? '✅ Réceptionné' : '⏳ En cours'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Articles */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text3)', marginBottom: 8 }}>Articles</div>
                  {Object.entries(ARTICLES_LABELS).map(([key, label]) => {
                    const val = order.articles?.[key] || 0;
                    if (val === 0) return null;
                    return (
                      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <span style={{ color: 'var(--text2)' }}>{label}</span>
                        <span style={{ fontWeight: 700, color: 'var(--text)' }}>{val}</span>
                      </div>
                    );
                  })}
                  {Object.values(order.articles || {}).every(v => !v || v === 0) && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>Aucun article</div>
                  )}
                </div>

                {/* Livraison */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text3)', marginBottom: 8 }}>Livraison Mondial Relay</div>
                  {[
                    ['Nom', `${order.livraison?.prenom || ''} ${order.livraison?.nom || ''}`],
                    ['Ville', `${order.livraison?.ville || ''} (${order.livraison?.code_postal || ''})`],
                    ['Téléphone', order.livraison?.telephone],
                    ['E-mail', order.livraison?.mail],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text2)' }}>{label}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{val || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {order.statut === 'en_cours' && (
                <button
                  onClick={() => handleConfirm(order.id)}
                  disabled={confirming === order.id}
                  style={{ marginTop: 16, padding: '10px 20px', background: '#3B6D11', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: confirming === order.id ? 'not-allowed' : 'pointer' }}
                >
                  {confirming === order.id ? 'Confirmation…' : '✅ Confirmer la réception'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
